function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import countBy from 'lodash/countBy';
import without from 'lodash/without';
import { Experiment, ExperimentFilterStatus } from 'apps/experiments/src/types';
import { SelectReact } from 'common/components/select';
import { useTranslation } from 'common/i18n/hooks';
import { SelectTheme } from 'common/components/select/constants';
import { ChangeEvent } from 'common/components/select/types';
const EXPERIMENT_FILTER_STATUSES: ExperimentFilterStatus[] = [ExperimentFilterStatus.CONCLUDED, ExperimentFilterStatus.ACTIVE, ExperimentFilterStatus.CONFIGURED, ExperimentFilterStatus.DRAFT, ExperimentFilterStatus.SUCCESS_CONTROL, ExperimentFilterStatus.SUCCESS_TREATMENT, ExperimentFilterStatus.FAIL];
export function useExperimentFilters(_ref: {
  experiments?: Experiment[];
  userId: number;
}) {
  let {
    experiments,
    userId
  }: {
    experiments?: Experiment[];
    userId: number;
  } = _ref;
  const i18n = useTranslation();
  const [creatorFilterState, setCreatorFilterState] = React.useState<{
    id: string;
    value: number[];
  }>({
    id: "creator_id",
    value: []
  });
  const [statusFilterState, setStatusFilterState] = React.useState<{
    id: string;
    value: ExperimentFilterStatus[];
  }>({
    id: "status",
    value: []
  });
  const experimentOwners = React.useMemo(() => {
    if (!experiments) {
      return [];
    }
    const experimentsWithCreators = experiments.filter(exp => exp.creator_id !== null);
    const creatorLabelMap = experimentsWithCreators.reduce((acc, exp) => {
      const creatorId = exp.creator_id;
      if (!acc[creatorId]) {
        acc[creatorId] = exp.creator_name || exp.creator_email || "Unknown";
      }
      return acc;
    }, {} as Record<number, string>);
    const creatorCounts = countBy(experiments.filter(exp => exp.creator_id !== null), exp => exp.creator_id);
    // make sure the current user is first in the list
    if (userId && creatorCounts[userId]) {
      creatorCounts[userId] = Number.MAX_SAFE_INTEGER;
    }
    const uniqueCreators = Object.entries(creatorCounts).map(_ref2 => {
      let [id, count] = _ref2;
      const creatorName = creatorLabelMap[Number(id)];
      return {
        id: Number(id),
        name: creatorName !== null && creatorName !== void 0 ? creatorName : "",
        count: count as number
      };
    }).sort((a, b) => b.count - a.count);
    return uniqueCreators;
  }, [experiments, userId]);
  const getCustomColumnFilters = React.useCallback((columnId: string) => {
    switch (columnId) {
      case "creator_id":
        {
          return <SelectReact theme={SelectTheme.Secondary} label={i18n.t("experiments.list.column-creator")} sections={[{
            items: experimentOwners.map(owner => {
              var _creatorFilterState$v;
              return {
                label: owner.name,
                value: owner.id,
                isSelected: (_creatorFilterState$v = creatorFilterState.value) === null || _creatorFilterState$v === void 0 ? void 0 : _creatorFilterState$v.includes(owner.id)
              };
            })
          }]} menuWidth={-1} multiple onChange={ev => {
            const selectedItem = (ev as ChangeEvent).detail.item;
            setCreatorFilterState(prev => _objectSpread(_objectSpread({}, prev), {}, {
              value: !selectedItem.isSelected ? [...prev.value, selectedItem.value] : without(prev.value, selectedItem.value)
            }));
          }} />;
        }
      case "status":
        {
          return <SelectReact theme={SelectTheme.Secondary} label={i18n.t("experiments.list.column-status")} sections={[{
            items: EXPERIMENT_FILTER_STATUSES.map(status => {
              var _statusFilterState$va;
              return {
                label: i18n.t("experiments.status.".concat(status)),
                value: status,
                isSelected: (_statusFilterState$va = statusFilterState.value) === null || _statusFilterState$va === void 0 ? void 0 : _statusFilterState$va.includes(status)
              };
            })
          }]} menuWidth={-1} multiple onChange={ev => {
            const selectedItem = (ev as ChangeEvent).detail.item;
            setStatusFilterState(prev => _objectSpread(_objectSpread({}, prev), {}, {
              value: !selectedItem.isSelected ? [...prev.value, selectedItem.value] : without(prev.value, selectedItem.value)
            }));
          }} />;
        }
      default:
        return null;
    }
  }, [experimentOwners, i18n, creatorFilterState, statusFilterState]);
  const handleFilterReset = React.useCallback(() => {
    setCreatorFilterState({
      id: "creator_id",
      value: []
    });
    setStatusFilterState({
      id: "status",
      value: []
    });
  }, []);
  return {
    filtersState: [creatorFilterState, statusFilterState],
    getCustomColumnFilters,
    handleFilterReset
  };
}